import { FormEvent, useState } from 'react';
import {
  Link as RouterLink,
  Navigate,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { Alert, Link, Stack, TextField, Typography } from '@mui/material';
import { ApiError } from '@/auth/api';
import { landingPath, safeNextPath, withNext } from '@/auth/nextPath';
import { useAuth } from '@/auth/useAuth';
import { NarrowPage } from '@/components/NarrowPage';
import { PageHeader } from '@/components/PageHeader';
import { PrimaryButton } from '@/components/PrimaryButton';
import { SectionCard } from '@/components/SectionCard';

/**
 * The single login for buyers and staff (#271). Nothing here mentions the panel: the session
 * decides where each one lands, and the backend still decides what each one may do.
 */
export function LoginPage() {
  const { isAuthenticated, login, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Set when a page sent the visitor here on purpose, like the quotation form (ADR 0008).
  const next = safeNextPath(searchParams.get('next'));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (isAuthenticated && user) {
    return <Navigate to={landingPath(user.role, next)} replace />;
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      const response = await login({ email, password });
      navigate(landingPath(response.user.role, next), { replace: true });
    } catch (error) {
      // Only a rejected credential means the data is wrong; a network or server failure must not
      // make anyone doubt a correct password.
      if (error instanceof ApiError && error.status === 429) {
        setErrorMessage(
          'Alcanzaste el límite de intentos. Esperá unos minutos y volvé a intentar.',
        );
      } else if (error instanceof ApiError && (error.status === 400 || error.status === 401)) {
        setErrorMessage('Email o contraseña incorrectos.');
      } else {
        setErrorMessage('No pudimos iniciar sesión. Volvé a intentar en unos minutos.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <NarrowPage maxWidth={600}>
      <PageHeader
        eyebrow="TU CUENTA PHONETEC"
        title="Ingresá a tu cuenta"
        description="Accedé con tu email y tu contraseña."
      />
      <SectionCard title="Inicio de sesión">
        <Stack component="form" spacing={2} onSubmit={handleSubmit}>
          {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
          <TextField
            label="Email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
            fullWidth
          />
          <TextField
            label="Contraseña"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
            fullWidth
          />
          <Typography variant="body2">
            <Link
              component={RouterLink}
              to="/account/forgot-password"
              state={{ email }}
              underline="hover"
            >
              ¿Olvidaste tu contraseña?
            </Link>
          </Typography>
          <PrimaryButton type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Ingresando...' : 'Ingresar'}
          </PrimaryButton>
          <Typography variant="body2" color="text.secondary">
            ¿Todavía no tenés cuenta?{' '}
            <Link component={RouterLink} to={withNext('/account/register', next)} underline="hover">
              Creala acá
            </Link>
          </Typography>
        </Stack>
      </SectionCard>
    </NarrowPage>
  );
}

/** The buyer's login before #271 (`/account/login`): links and bookmarks keep working. */
export function LegacyLoginRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/login${search}`} replace />;
}
